import type { AgentSessionsQuery, AgentSessionsResponse } from './agentSessions.api';

type Listener = (response: AgentSessionsResponse) => void;
/** Bounded, document-lifetime cache. Scope identifies account, user and loading mode. */
export function createAgentSessionsCache() {
  const entries = new Map<
    string,
    {
      response?: AgentSessionsResponse;
      at: number;
      pending?: Promise<AgentSessionsResponse>;
      listeners: Set<Listener>;
    }
  >();
  const keyFor = (scope: string, query: AgentSessionsQuery) =>
    JSON.stringify([scope, query.from, query.to, query.userId ?? null]);
  return {
    peek(scope: string, query: AgentSessionsQuery) {
      return entries.get(keyFor(scope, query))?.response;
    },
    load(
      scope: string,
      query: AgentSessionsQuery,
      fetch: (publish: Listener) => Promise<AgentSessionsResponse>,
      listener?: Listener
    ) {
      const key = keyFor(scope, query);
      const previous = entries.get(key);
      if (previous?.pending) {
        if (listener) {
          previous.listeners.add(listener);
          if (previous.response) listener(previous.response);
        }
        return previous.pending;
      }
      if (previous?.response && Date.now() - previous.at < 1000)
        return Promise.resolve(previous.response);
      const entry = {
        response: previous?.response,
        at: previous?.at ?? 0,
        pending: undefined as Promise<AgentSessionsResponse> | undefined,
        listeners: new Set(listener ? [listener] : []),
      };
      entries.delete(key);
      entries.set(key, entry);
      while (entries.size > 8) entries.delete(entries.keys().next().value!);
      const publish: Listener = response => {
        // Mutations detach the cache, but active subscribers still need new pages.
        // Their session hook preserves local edits while merging these responses.
        if (
          entries.get(key) === entry &&
          (!entry.response || entry.response.next_cursor || !response.next_cursor)
        )
          entry.response = response;
        for (const subscriber of entry.listeners) subscriber(response);
      };
      entry.pending = fetch(publish)
        .then(response => {
          publish(response);
          entry.at = Date.now();
          return response;
        })
        .finally(() => {
          entry.pending = undefined;
          entry.listeners.clear();
        });
      return entry.pending;
    },
    invalidate() {
      // Detached requests cannot repopulate the cache after a mutation.
      entries.clear();
    },
  };
}
